"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

export default function SmoothScrollHandler() {
  const pathname = usePathname();
  const previousPathname = useRef(pathname);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleSmoothScroll = () => {
      const hash = window.location.hash;
      if (hash) {
        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        const attemptScroll = (retries = 0) => {
          const element = document.querySelector(hash) as HTMLElement | null;
          if (element) {
            const rect = element.getBoundingClientRect();
            const isElementReady = rect.height > 0 && rect.width > 0;
            if (isElementReady) {
              // Use native behavior with CSS scroll-margin to avoid double-scroll/jitter
              element.scrollIntoView({ behavior: "auto", block: "start" });
            } else if (retries < 20) {
              setTimeout(() => attemptScroll(retries + 1), 50);
            }
          } else if (retries < 20) {
            setTimeout(() => attemptScroll(retries + 1), 50);
          }
        };

        // Small delay to allow layout to settle after navigation
        const isCrossPageNavigation = previousPathname.current !== pathname;
        const delay = isCrossPageNavigation ? 200 : 50;
        scrollTimeoutRef.current = setTimeout(() => attemptScroll(), delay);
      }
    };

    // Handle smooth scroll on pathname change (cross-page navigation)
    handleSmoothScroll();

    const handleHashChange = () => {
      // Run once quickly; rely on CSS scroll-margin for correct offset
      handleSmoothScroll();
    };

    window.addEventListener("hashchange", handleHashChange);

    // Update previous pathname for next comparison
    previousPathname.current = pathname;

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [pathname]);

  return null;
}
